import { AppIcon, AppIcons } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.survey.output.blueprintSection.ownership.detail;

const METRIC_CARDS: { heading: string; items: readonly string[]; icon: AppIcons }[] = [
  { heading: STRINGS.rewardsHeading, items: STRINGS.rewards, icon: 'Check' },
  { heading: STRINGS.challengesHeading, items: STRINGS.challenges, icon: 'Flag04' },
];

export function OwnershipDetail() {
  return (
    <Box sx={containerSx}>
      <Typography variant="display-xs-semibold">{STRINGS.heading}</Typography>

      {/* Defining Ownership card */}
      <Box sx={definingCardSx}>
        <Box sx={definingContentSx}>
          {/* Badge */}
          <Box sx={badgeSx}>
            <AppIcon iconName="Webcam01" diameter="12px" color="Brand/700" />
            <Typography variant="text-sm-medium" sx={badgeTextSx}>
              {STRINGS.definingOwnershipBadge}
            </Typography>
          </Box>

          {/* Two-column text */}
          <Box sx={definingTextSx}>
            <Typography variant="text-sm-regular" sx={definingParagraphSx}>
              {STRINGS.definingOwnershipBody}
            </Typography>
            <Typography variant="text-sm-regular" component="div" sx={definingParagraphSx}>
              <Typography variant="text-sm-semibold" component="span" sx={definingParagraphSx}>
                {STRINGS.whyItMattersLabel}
              </Typography>
              {STRINGS.definingOwnershipWhyItMatters}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Two-column: Rewards | Challenges */}
      <Box sx={twoColumnSx}>
        {METRIC_CARDS.map(({ heading, items, icon }) => (
          <Box key={heading} sx={metricCardSx}>
            <Box sx={metricHeaderSx}>
              <Typography variant="text-sm-semibold">{heading}</Typography>
            </Box>
            <Box sx={metricContentSx}>
              <Box sx={metricListSx}>
                {items.map((item) => (
                  <Box key={item} sx={metricItemSx}>
                    <AppIcon iconName={icon} diameter="16px" color="Brand/500" />
                    <Typography variant="text-sm-medium" sx={metricItemTextSx}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// --- Styles ---

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  gap: backrs.spacing['spacing-3xl'],
});

// --- Defining Ownership card ---

const definingCardSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const definingContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  padding: `${backrs.spacing['spacing-2xl']} ${backrs.spacing['spacing-xl']}`,
});

const badgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  gap: backrs.spacing['spacing-xs'],
  paddingLeft: backrs.spacing['spacing-sm'],
  paddingRight: backrs.spacing['spacing-md'],
  paddingTop: backrs.spacing['spacing-xxs'],
  paddingBottom: backrs.spacing['spacing-xxs'],
  borderRadius: backrs.radius['radius-sm'],
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
});

const badgeTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
});

const definingTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  '@container (min-width: 500px)': {
    flexDirection: 'row',
    gap: backrs.spacing['spacing-lg'],
  },
});

const definingParagraphSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

// --- Metric cards (Rewards / Challenges) ---

const twoColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 500px)': {
    flexDirection: 'row',
    gap: backrs.spacing['spacing-xl'],
  },
});

const metricCardSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-subtle'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'clip',
});

const metricHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  paddingTop: backrs.spacing['spacing-lg'],
  paddingBottom: backrs.spacing['spacing-md'],
  paddingLeft: backrs.spacing['spacing-2xl'],
  paddingRight: backrs.spacing['spacing-2xl'],
});

const metricContentSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  padding: backrs.spacing['spacing-2xl'],
});

const metricListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const metricItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xs'],
});

const metricItemTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Base/Black'],
});
